import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCheck,
  Loader2,
  MessageSquarePlus,
  MoreVertical,
  Plus,
  Search,
  Send,
  Settings,
  Smile,
  Users,
} from 'lucide-react';
import { apiClient } from '@/utils/api';
import { useAuthStore } from '@/store/authStore';
import { useSettings } from '@/hooks/useSettings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { StaffMessageBody } from '@/components/staff-messaging/StaffMessageBody';
import { STAFF_EMOJI_CATEGORIES } from '@/components/staff-messaging/curatedEmojis';
import { countGraphemes, STAFF_MESSAGE_MAX_GRAPHEMES, validateStaffMessageBody } from '@/utils/graphemeCount';
import { getAppSocket } from '@/services/socketRegistry';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type StaffMsg = {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  createdAt: string;
  deleted?: boolean;
  deliveredAt: string | null;
};

type ConvRow = {
  id: string;
  type: 'DIRECT' | 'GROUP';
  title: string;
  updatedAt: string;
  unreadCount: number;
  lastMessage: { id: string; bodyPreview: string; createdAt: string; senderId: string } | null;
};

type ConvDetail = {
  id: string;
  type: 'DIRECT' | 'GROUP';
  title: string | null;
  ownerId: string | null;
  displayTitle: string;
  dmPeerLastReadAt: string | null;
  members: { userId: string; lastReadAt: string | null; user: { id: string; firstName: string | null; lastName: string | null; email: string } }[];
};

type AssignableUser = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
};

function displayName(u: { firstName?: string | null; lastName?: string | null; email?: string }) {
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return n || u.email || 'Utente';
}

function tickState(
  msg: StaffMsg,
  isDm: boolean,
  isMine: boolean,
  peerLastReadIso: string | null,
): 'none' | 'sent' | 'delivered' | 'read' {
  if (!isDm || !isMine || msg.deleted) return 'none';
  const created = new Date(msg.createdAt).getTime();
  if (peerLastReadIso && new Date(peerLastReadIso).getTime() >= created) return 'read';
  if (msg.deliveredAt) return 'delivered';
  return 'sent';
}

function MessageBubble(props: {
  m: StaffMsg;
  mine: boolean;
  detail: ConvDetail;
  peerLastRead: string | null;
  userId?: string;
  onForward: (m: StaffMsg) => void;
  onDelete: (m: StaffMsg) => void;
  onVisibleForAck: (id: string) => void;
}) {
  const { m, mine, detail, peerLastRead, userId, onForward, onDelete, onVisibleForAck } = props;
  const elRef = useRef<HTMLDivElement>(null);
  const t = tickState(m, detail.type === 'DIRECT', mine, peerLastRead);

  useEffect(() => {
    if (!elRef.current || detail.type !== 'DIRECT' || mine || m.deleted) return;
    const el = elRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onVisibleForAck(m.id);
      },
      { threshold: 0.45 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [detail.type, m.id, m.deleted, mine, onVisibleForAck]);

  return (
    <div ref={elRef} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
          mine ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
        }`}
      >
        <div className="whitespace-pre-wrap break-words">
          {m.deleted ? (
            <span className="italic opacity-80">Messaggio eliminato</span>
          ) : (
            <StaffMessageBody text={m.body} />
          )}
        </div>
        <div
          className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${
            mine ? 'text-blue-100' : 'text-gray-500'
          }`}
        >
          <span>
            {new Date(m.createdAt).toLocaleTimeString('it-IT', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {t !== 'none' && (
            <span className="inline-flex items-center gap-0" aria-hidden>
              {t === 'sent' && <Check className="h-3.5 w-3.5 text-blue-200/90" />}
              {t === 'delivered' && <CheckCheck className="h-3.5 w-3.5 text-blue-200/90" />}
              {t === 'read' && <CheckCheck className="h-3.5 w-3.5 text-sky-200" />}
            </span>
          )}
          {!m.deleted && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="ml-1 rounded p-0.5 hover:bg-white/10"
                  aria-label="Azioni messaggio"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!!m.deleted}
                  onClick={() => onForward(m)}
                >
                  Inoltra…
                </DropdownMenuItem>
                {mine && userId && m.senderId === userId && (
                  <DropdownMenuItem className="text-red-600" onClick={() => onDelete(m)}>
                    Elimina…
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StaffMessagingPage() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get('conversationId') || '';
  const { settings } = useSettings();
  const tz = settings?.general?.timezone || 'Europe/Rome';

  const [listFilter, setListFilter] = useState('');
  const [composer, setComposer] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);

  const [newDmOpen, setNewDmOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState('');
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberId, setAddMemberId] = useState<string>('');

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState<string>('');

  const [forwardMsg, setForwardMsg] = useState<StaffMsg | null>(null);
  const [forwardDest, setForwardDest] = useState<string>('');

  const [deleteMsg, setDeleteMsg] = useState<StaffMsg | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const stickBottomRef = useRef(true);
  const ackedRef = useRef<Set<string>>(new Set());

  const { data: convList = [], isLoading: listLoading } = useQuery({
    queryKey: ['staff-messaging', 'conversations'],
    queryFn: async () => {
      const r = await apiClient.get('/staff-messaging/conversations');
      return r.data.data as ConvRow[];
    },
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['staff-messaging', 'detail', conversationId],
    queryFn: async () => {
      const r = await apiClient.get(`/staff-messaging/conversations/${conversationId}`);
      return r.data.data as ConvDetail;
    },
    enabled: !!conversationId,
  });

  const {
    data: pagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['staff-messaging', 'messages', conversationId],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const r = await apiClient.get(`/staff-messaging/conversations/${conversationId}/messages`, {
        params: { limit: 50, cursor: pageParam },
      });
      return r.data.data as { messages: StaffMsg[]; nextCursor: string | null };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!conversationId,
  });

  const messages = useMemo(() => {
    const pages = pagesData?.pages ?? [];
    const out: StaffMsg[] = [];
    for (let i = pages.length - 1; i >= 0; i--) {
      out.push(...pages[i].messages);
    }
    return out;
  }, [pagesData]);

  const filteredList = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    if (!q) return convList;
    return convList.filter((c) => c.title.toLowerCase().includes(q));
  }, [convList, listFilter]);

  const { data: assignable = [] } = useQuery({
    queryKey: ['users', 'assignable'],
    queryFn: async () => {
      const r = await apiClient.get('/users/assignable');
      return r.data.data as AssignableUser[];
    },
  });

  const peerLastRead = detail?.type === 'DIRECT' ? detail.dmPeerLastReadAt : null;

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };

  useEffect(() => {
    stickBottomRef.current = true;
    ackedRef.current = new Set();
    scrollToBottom();
  }, [conversationId]);

  useEffect(() => {
    if (stickBottomRef.current) {
      scrollToBottom();
    }
  }, [messages.length]);

  const markRead = useCallback(async () => {
    if (!conversationId) return;
    try {
      await apiClient.post(`/staff-messaging/conversations/${conversationId}/read`);
      void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
    } catch {
      /* ignore */
    }
  }, [conversationId, qc]);

  useEffect(() => {
    if (!conversationId || !messages.length) return;
    const el = scrollRef.current;
    if (!el) return;
    const few = el.scrollHeight <= el.clientHeight + 8;
    const t = window.setTimeout(() => {
      if (few) void markRead();
    }, 400);
    return () => window.clearTimeout(t);
  }, [conversationId, messages.length, markRead]);

  const onScrollArea = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    stickBottomRef.current = scrollHeight - scrollTop - clientHeight < 80;
    if (stickBottomRef.current) void markRead();

    if (scrollTop < 120 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const sendDeliveryAck = useCallback(
    (ids: string[]) => {
      if (!conversationId || detail?.type !== 'DIRECT' || !ids.length) return;
      const socket = getAppSocket();
      const fresh = ids.filter((id) => !ackedRef.current.has(id));
      if (!fresh.length) return;
      fresh.forEach((id) => ackedRef.current.add(id));
      if (socket?.connected) {
        socket.emit('staff:deliveryAck', { conversationId, messageIds: fresh });
      }
      void apiClient
        .post(`/staff-messaging/conversations/${conversationId}/messages/${fresh[0]}/delivered`, {
          messageIds: fresh,
        })
        .catch(() => {
          fresh.forEach((id) => ackedRef.current.delete(id));
        });
    },
    [conversationId, detail?.type],
  );

  const sendMessage = async () => {
    const v = validateStaffMessageBody(composer);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }
    if (!conversationId) return;
    try {
      await apiClient.post(`/staff-messaging/conversations/${conversationId}/messages`, {
        body: composer,
      });
      setComposer('');
      void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
      stickBottomRef.current = true;
      setTimeout(scrollToBottom, 50);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Invio fallito');
    }
  };

  const createDm = async (otherId: string) => {
    try {
      const r = await apiClient.post('/staff-messaging/conversations/direct', { userId: otherId });
      const id = r.data.data.id as string;
      setNewDmOpen(false);
      setSearchParams({ conversationId: id });
      void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Errore');
    }
  };

  const createGroup = async () => {
    try {
      const r = await apiClient.post('/staff-messaging/conversations/group', {
        title: groupTitle,
        memberIds: selectedMembers,
      });
      const id = r.data.data.id as string;
      setNewGroupOpen(false);
      setGroupTitle('');
      setSelectedMembers([]);
      setSearchParams({ conversationId: id });
      void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Errore');
    }
  };

  const doDelete = async () => {
    if (!deleteMsg || !conversationId) return;
    try {
      await apiClient.delete(
        `/staff-messaging/conversations/${conversationId}/messages/${deleteMsg.id}`,
      );
      setDeleteMsg(null);
      void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Eliminazione non consentita');
    }
  };

  const groupConversations = useMemo(
    () => convList.filter((c) => c.type === 'GROUP'),
    [convList],
  );

  const forwardUserOptions = useMemo(
    () => assignable.filter((u) => u.id !== user?.id),
    [assignable, user?.id],
  );

  const doForward = async () => {
    if (!forwardMsg || forwardMsg.deleted || !forwardDest) return;
    const sender = assignable.find((u) => u.id === forwardMsg.senderId);
    const senderLabel = sender ? displayName(sender) : 'Utente';
    const when = new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: tz,
    }).format(new Date(forwardMsg.createdAt));
    const prefix = `Inoltrato da ${senderLabel} · ${when}\n\n`;
    const body = `${prefix}${forwardMsg.body || ''}`;
    try {
      let targetConvId: string;
      if (forwardDest.startsWith('user:')) {
        const otherUserId = forwardDest.slice(5);
        const r = await apiClient.post('/staff-messaging/conversations/direct', { userId: otherUserId });
        targetConvId = r.data.data.id as string;
      } else if (forwardDest.startsWith('group:')) {
        targetConvId = forwardDest.slice(6);
      } else {
        return;
      }
      await apiClient.post(`/staff-messaging/conversations/${targetConvId}/messages`, { body });
      setForwardMsg(null);
      setForwardDest('');
      setSearchParams({ conversationId: targetConvId });
      void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
      toast.success('Messaggio inoltrato');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Inoltro fallito');
    }
  };

  const isOwner = detail?.type === 'GROUP' && detail.ownerId === user?.id;

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-[480px] gap-4">
      {/* Lista */}
      <div className="flex min-h-0 w-full max-w-md flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b p-3">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-8"
              placeholder="Filtra conversazioni…"
              value={listFilter}
              onChange={(e) => setListFilter(e.target.value)}
              aria-label="Filtra conversazioni"
            />
          </div>
          <Button size="icon" variant="outline" title="Nuova DM" onClick={() => setNewDmOpen(true)}>
            <MessageSquarePlus className="h-5 w-5" />
          </Button>
          <Button size="icon" variant="outline" title="Nuovo gruppo" onClick={() => setNewGroupOpen(true)}>
            <Users className="h-5 w-5" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ul className="divide-y">
            {listLoading && (
              <li className="flex justify-center p-6 text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin" />
              </li>
            )}
            {!listLoading &&
              filteredList.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`flex w-full flex-col gap-0.5 px-3 py-2.5 text-left hover:bg-gray-50 ${
                      c.id === conversationId ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => setSearchParams({ conversationId: c.id })}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium text-gray-900">{c.title}</span>
                      {c.unreadCount > 0 && (
                        <span className="shrink-0 rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">
                          {c.unreadCount > 99 ? '99+' : c.unreadCount}
                        </span>
                      )}
                    </div>
                    {c.lastMessage && (
                      <span className="truncate text-xs text-gray-500">{c.lastMessage.bodyPreview}</span>
                    )}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      </div>

      {/* Thread */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
        {!conversationId && (
          <div className="flex flex-1 flex-col items-center justify-center text-gray-500">
            <p>Seleziona una conversazione o creane una nuova.</p>
          </div>
        )}

        {conversationId && detailLoading && (
          <div className="flex flex-1 items-center justify-center text-gray-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        {conversationId && detail && !detailLoading && (
          <>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{detail.displayTitle}</h2>
                {detail.type === 'GROUP' && (
                  <p className="text-xs text-gray-500">{detail.members.length} membri</p>
                )}
              </div>
              {detail.type === 'GROUP' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Impostazioni gruppo">
                      <Settings className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isOwner && (
                      <>
                        <DropdownMenuItem
                          onClick={() => {
                            setRenameVal(detail.title || '');
                            setRenameOpen(true);
                          }}
                        >
                          Rinomina gruppo
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setAddMemberId('');
                            setAddMemberOpen(true);
                          }}
                        >
                          Aggiungi membro
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setTransferTo('');
                            setTransferOpen(true);
                          }}
                        >
                          Trasferisci proprietà
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={async () => {
                        if (!window.confirm('Uscire dal gruppo?')) return;
                        try {
                          await apiClient.post(`/staff-messaging/conversations/${conversationId}/leave`);
                          setSearchParams({});
                          void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
                        } catch (e: any) {
                          toast.error(e.response?.data?.message || 'Operazione non consentita');
                        }
                      }}
                    >
                      Esci dal gruppo
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <div
              ref={scrollRef}
              className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
              onScroll={onScrollArea}
            >
              {isFetchingNextPage && (
                <div className="flex justify-center py-2 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}
              {messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  m={m}
                  mine={m.senderId === user?.id}
                  detail={detail}
                  peerLastRead={peerLastRead}
                  userId={user?.id}
                  onForward={(msg) => {
                    setForwardMsg(msg);
                    setForwardDest('');
                  }}
                  onDelete={setDeleteMsg}
                  onVisibleForAck={(id) => sendDeliveryAck([id])}
                />
              ))}
            </div>

            <div className="border-t p-3">
              <div className="flex gap-2">
                <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="icon" aria-label="Emoji">
                      <Smile className="h-5 w-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="max-h-64 overflow-y-auto p-2">
                      {STAFF_EMOJI_CATEGORIES.map((cat) => (
                        <div key={cat.label} className="mb-3">
                          <p className="mb-1 text-xs font-medium text-gray-500">{cat.label}</p>
                          <div className="flex flex-wrap gap-1">
                            {cat.emojis.map((em) => (
                              <button
                                key={em}
                                type="button"
                                className="rounded p-1 text-lg hover:bg-gray-100"
                                onClick={() => {
                                  setComposer((prev) => prev + em);
                                  setEmojiOpen(false);
                                }}
                              >
                                {em}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Textarea
                  className="min-h-[44px] flex-1 resize-none"
                  placeholder="Scrivi un messaggio… (Invio per inviare, Maiusc+Invio nuova riga)"
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void sendMessage();
                    }
                  }}
                  aria-label="Messaggio"
                />
                <div className="flex flex-col items-end gap-1">
                  <Button type="button" onClick={() => void sendMessage()}>
                    <Send className="h-4 w-4" />
                  </Button>
                  <span className="text-[10px] text-gray-400">
                    {countGraphemes(composer)}/{STAFF_MESSAGE_MAX_GRAPHEMES}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={newDmOpen} onOpenChange={setNewDmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova conversazione</DialogTitle>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto">
            <ul className="space-y-1">
              {assignable
                .filter((u) => u.id !== user?.id)
                .map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      className="w-full rounded-md px-2 py-2 text-left hover:bg-gray-50"
                      onClick={() => void createDm(u.id)}
                    >
                      {displayName(u)}
                      <span className="ml-2 text-xs text-gray-400">{u.email}</span>
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={newGroupOpen} onOpenChange={setNewGroupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo gruppo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="gtitle">Titolo</Label>
              <Input
                id="gtitle"
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                placeholder="Nome gruppo"
              />
            </div>
            <div>
              <Label>Altri membri (minimo 2 oltre a te)</Label>
              <div className="h-48 overflow-y-auto rounded border p-2">
                {assignable
                  .filter((u) => u.id !== user?.id)
                  .map((u) => (
                    <label key={u.id} className="flex cursor-pointer items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(u.id)}
                        onChange={(e) => {
                          setSelectedMembers((prev) =>
                            e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id),
                          );
                        }}
                      />
                      {displayName(u)}
                    </label>
                  ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setNewGroupOpen(false)}>
              Annulla
            </Button>
            <Button onClick={() => void createGroup()} disabled={!groupTitle.trim() || selectedMembers.length < 2}>
              <Plus className="mr-1 h-4 w-4" />
              Crea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rinomina gruppo</DialogTitle>
          </DialogHeader>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} />
          <DialogFooter>
            <Button
              onClick={async () => {
                try {
                  await apiClient.patch(`/staff-messaging/conversations/${conversationId}`, {
                    title: renameVal,
                  });
                  setRenameOpen(false);
                  void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
                } catch (e: any) {
                  toast.error(e.response?.data?.message || 'Errore');
                }
              }}
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi membro</DialogTitle>
          </DialogHeader>
          <Select value={addMemberId} onValueChange={setAddMemberId}>
            <SelectTrigger>
              <SelectValue placeholder="Utente" />
            </SelectTrigger>
            <SelectContent>
              {assignable
                .filter((u) => u.id !== user?.id && !detail?.members.some((m) => m.userId === u.id))
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {displayName(u)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              onClick={async () => {
                if (!addMemberId) return;
                try {
                  await apiClient.post(`/staff-messaging/conversations/${conversationId}/members`, {
                    userId: addMemberId,
                  });
                  setAddMemberOpen(false);
                  void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
                } catch (e: any) {
                  toast.error(e.response?.data?.message || 'Errore');
                }
              }}
            >
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trasferisci proprietà</DialogTitle>
          </DialogHeader>
          <Select value={transferTo} onValueChange={setTransferTo}>
            <SelectTrigger>
              <SelectValue placeholder="Nuovo proprietario" />
            </SelectTrigger>
            <SelectContent>
              {detail?.members
                .filter((m) => m.userId !== user?.id)
                .map((m) => (
                  <SelectItem key={m.userId} value={m.userId}>
                    {displayName(m.user)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              onClick={async () => {
                if (!transferTo) return;
                try {
                  await apiClient.post(`/staff-messaging/conversations/${conversationId}/transfer-owner`, {
                    newOwnerId: transferTo,
                  });
                  setTransferOpen(false);
                  void qc.invalidateQueries({ queryKey: ['staff-messaging'] });
                } catch (e: any) {
                  toast.error(e.response?.data?.message || 'Errore');
                }
              }}
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!forwardMsg} onOpenChange={(o) => !o && setForwardMsg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inoltra messaggio</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Scegli un <strong>utente</strong> (se non avete ancora una chat, ne viene creata una) oppure un{' '}
            <strong>gruppo</strong> di cui fai parte.
          </p>
          <Select value={forwardDest} onValueChange={setForwardDest}>
            <SelectTrigger>
              <SelectValue placeholder="Destinazione" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectGroup>
                <SelectLabel>Utenti (nuova o DM esistente)</SelectLabel>
                {forwardUserOptions.map((u) => (
                  <SelectItem key={`u-${u.id}`} value={`user:${u.id}`}>
                    {displayName(u)} — {u.email}
                  </SelectItem>
                ))}
              </SelectGroup>
              {groupConversations.length > 0 && (
                <>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>I tuoi gruppi</SelectLabel>
                    {groupConversations.map((c) => (
                      <SelectItem key={`g-${c.id}`} value={`group:${c.id}`}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setForwardMsg(null)}>
              Annulla
            </Button>
            <Button onClick={() => void doForward()} disabled={!forwardDest}>
              Inoltra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteMsg} onOpenChange={(o) => !o && setDeleteMsg(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo messaggio per tutti?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => void doDelete()}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
